import React, { useState, useEffect } from 'react';
import {
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { withRouter, useParams } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import API helper
import httprequester from '../../lib/httprequester';

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'UPCOMING': return 'warning';
      case 'ONGOING': return 'success';
      case 'COMPLETED': return 'secondary';
      default: return 'light';
    }
  };

  return (
    <Badge color={getStatusColor(status)} className="fs-6">
      {status}
    </Badge>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired
};

// Speaker/Specialist card component
const SpeakerCard = ({ item, type }) => (
  <Card className="mb-3">
    <CardBody>
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <h6 className="mb-1">
            {type === 'speaker' ? item.name : item.specialist_name}
          </h6>
          {type === 'specialist' && (
            <p className="text-muted mb-1">Order: {item.specialist_order}</p>
          )}
        </div>
      </div>
    </CardBody>
  </Card>
);

SpeakerCard.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    specialist_name: PropTypes.string,
    specialist_order: PropTypes.number
  }).isRequired,
  type: PropTypes.string.isRequired
};

const DetailWebinar = ({ history }) => {
  const { id } = useParams();

  // State management
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    webinar: null
  });
  const [emailModal, setEmailModal] = useState({
    show: false,
    webinar: null
  });
  const [emailConfirmation, setEmailConfirmation] = useState({
    show: false,
    webinar: null,
    triggerType: null,
    emailType: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [blastId, setBlastId] = useState(null);
  const [blastProgress, setBlastProgress] = useState({ total: 0, sent: 0, failed: 0 });
  const [isPolling, setIsPolling] = useState(false);

  // Fetch webinar details from API
  const fetchWebinarDetails = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await httprequester.getAsync(`webinar/cms/webinars/${id}`);

      if (response.data) {
        setWebinar(response.data);
      } else {
        setError('Failed to fetch webinar details');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Webinar not found');
      } else {
        setError('Error fetching webinar details');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load webinar details on component mount
  useEffect(() => {
    if (id) {
      fetchWebinarDetails();
    }
  }, [id]);

  // Navigation handlers
  const handleEdit = () => {
    history.push(`/cme-online/edit/${id}`);
  };

  const handleBack = () => {
    history.push('/cme-online');
  };

  // Delete webinar
  const handleDelete = async () => {
    if (!deleteModal.webinar) return;

    try {
      const response = await httprequester.deleteAsync(`webinar/cms/webinars/${id}`);

      if (response.data && response.data.success) {
        history.push('/cme-online');
      } else {
        setError(response.data?.message || 'Failed to delete webinar');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Cannot delete webinar with registrations');
      } else if (err.response?.status === 404) {
        setError('Webinar not found');
      } else {
        setError('Error deleting webinar');
      }
      console.error('Error:', err);
    }
  };

  // Show email options modal
  const handleSendEmail = () => {
    if (!webinar?.webinar_id) return;
    setEmailModal({ show: true, webinar });
  };

  // Show confirmation dialog for email sending
  const showEmailConfirmation = (triggerType, emailType) => {
    if (!emailModal.webinar?.webinar_id) return;
    setEmailConfirmation({
      show: true,
      webinar: emailModal.webinar,
      triggerType,
      emailType
    });
  };

  // Actually send bulk email with specific trigger type
  const confirmSendBulkEmail = async () => {
    if (!emailConfirmation.webinar?.webinar_id || !emailConfirmation.triggerType) return;

    setEmailLoading(true);
    setEmailSuccess('');
    setError('');
    setEmailConfirmation({ show: false, webinar: null, triggerType: null, emailType: '' });
    setEmailModal({ show: false, webinar: null });

    try {
      const response = await httprequester.postAsync('webinar/cms/send-bulk-email', {
        webinar_id: emailConfirmation.webinar.webinar_id,
        trigger_type: emailConfirmation.triggerType
      });

      if (response.data) {
        const { message, data } = response.data;
        setEmailSuccess(`${message} (${data.emails_sent}/${data.approved_participants_count} participants)`);
        if (data.blast_id) {
          setBlastId(data.blast_id);
          setBlastProgress({
            total: data.approved_participants_count || 0,
            sent: data.emails_sent || 0,
            failed: data.emails_failed || 0
          });
          startPollingBlastStatus(data.blast_id);
        }
      } else {
        setError('Failed to send emails');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Webinar not found');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Invalid request');
      } else {
        setError('Error sending emails');
      }
      console.error('Error:', err);
    } finally {
      setEmailLoading(false);
    }
  };

  // Polling helper to fetch blast status periodically
  const startPollingBlastStatus = (id) => {
    if (!id) return;
    setIsPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await httprequester.getAsync(`webinar/cms/send-bulk-email/${id}/status`);
        if (res?.data?.data) {
          const d = res.data.data;
          const total = Number(d.total || 0);
          const sent = Number(d.sent || 0);
          const failed = Number(d.failed || 0);
          setBlastProgress({ total, sent, failed });

          // Stop criteria: completed_at present or sent+failed >= total (and total > 0)
          if (d.completed_at || (total > 0 && sent + failed >= total)) {
            clearInterval(interval);
            setIsPolling(false);
            setEmailSuccess(`Bulk email completed (${sent}/${total} sent, ${failed} failed)`);
          }
        }
      } catch (e) {
        // Stop polling if server says not found
        if (e?.response?.status === 404) {
          clearInterval(interval);
          setIsPolling(false);
        }
        // otherwise keep trying silently
      }
    }, 2000);

    // Safety stop after 15 minutes
    setTimeout(() => {
      clearInterval(interval);
      setIsPolling(false);
    }, 15 * 60 * 1000);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Clear alerts
  const clearAlert = () => setError('');

  // PropTypes
  DetailWebinar.propTypes = {
    history: PropTypes.object.isRequired
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-3">Loading webinar details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger" isOpen={!!error} toggle={clearAlert}>
            {error}
          </Alert>
          <Button color="secondary" onClick={handleBack}>
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  if (!webinar) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning">
            Webinar not found
          </Alert>
          <Button color="secondary" onClick={handleBack}>
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{webinar.title} | CME Online | Ruang Ilmiah CMS</title>
        </MetaTags>

        <div className="container-fluid">
          <Breadcrumbs
            title="CME Online"
            breadcrumbItem="Webinar Details"
          />

          {/* Success Alert for Email */}
          {emailSuccess && (
            <Alert color="success" isOpen={!!emailSuccess} toggle={() => setEmailSuccess('')}>
              <i className="bx bx-check-circle me-2"></i>
              {emailSuccess}
            </Alert>
          )}

          {/* Header Actions */}
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Webinar Details</h4>
                <div className="d-flex gap-2">
                  <Button color="secondary" onClick={handleBack}>
                    <i className="bx bx-arrow-back me-1"></i>
                    Back to List
                  </Button>
                  <Button
                    color="info"
                    onClick={handleSendEmail}
                    disabled={emailLoading}
                  >
                    {emailLoading ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-envelope me-1"></i>
                        Send Info
                      </>
                    )}
                  </Button>
                  <Button color="primary" onClick={handleEdit}>
                    <i className="bx bx-edit me-1"></i>
                    Edit Webinar
                  </Button>
                  <Button
                    color="danger"
                    onClick={() => setDeleteModal({ show: true, webinar })}
                  >
                    <i className="bx bx-trash me-1"></i>
                    Delete
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Main Content */}
          <Row>
            {/* Left Column - Main Details */}
            <Col lg="8">
              {/* Basic Information */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">Basic Information</h5>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Title</label>
                        <p className="mb-0">{webinar.title}</p>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Slug</label>
                        <p className="mb-0">{webinar.slug}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Status</label>
                        <div>
                          <StatusBadge status={webinar.status} />
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Event Date</label>
                        <p className="mb-0">{formatDisplayDate(webinar.date)}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Quota</label>
                        <p className="mb-0">{webinar.quota ? `${webinar.quota} participants` : 'Unlimited'}</p>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Registration Status</label>
                        <div>
                          <StatusBadge status={webinar.status} />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <div
                      className="mb-0"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {webinar.desc || 'No description available'}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* CME Points */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">CME Points</h5>
                  <Row>
                    <Col md="6">
                      <div className="text-center p-3 bg-light rounded">
                        <h3 className="text-primary mb-1">{webinar.skp || 0}</h3>
                        <p className="mb-0 text-muted">SKP Points</p>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="text-center p-3 bg-light rounded">
                        <h3 className="text-success mb-1">{webinar.Point || 0}</h3>
                        <p className="mb-0 text-muted">CME Points</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* URLs */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">Links & URLs</h5>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Live Meeting URL</label>
                        {webinar.live_url ? (
                          <div>
                            <a
                              href={webinar.live_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-break"
                            >
                              {webinar.live_url}
                            </a>
                          </div>
                        ) : (
                          <p className="text-muted mb-0">Not available</p>
                        )}
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Video Recording URL</label>
                        {webinar.video_url ? (
                          <div>
                            <a
                              href={webinar.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-break"
                            >
                              {webinar.video_url}
                            </a>
                          </div>
                        ) : (
                          <p className="text-muted mb-0">Not available</p>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Additional Details */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">Additional Details</h5>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Webinar ID</label>
                        <p className="mb-0">{webinar.webinar_id}</p>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Created By</label>
                        <p className="mb-0">{webinar.created_by || '-'}</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column - Additional Info */}
            <Col lg="4">
              {/* Thumbnail */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">Thumbnail</h5>
                  {webinar.thumbnail ? (
                    <img
                      src={webinar.thumbnail}
                      alt={webinar.title}
                      className="img-fluid rounded"
                      style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = '/default-thumbnail.png';
                        e.target.onerror = null;
                      }}
                    />
                  ) : (
                    <div className="text-center p-4 bg-light rounded">
                      <i className="bx bx-image fs-1 text-muted"></i>
                      <p className="text-muted mb-0 mt-2">No thumbnail available</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Speakers */}
              {webinar.speakers && webinar.speakers.length > 0 && (
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-3">Speakers</h5>
                    {webinar.speakers.map((speaker, index) => (
                      <SpeakerCard
                        key={speaker.speaker_id || index}
                        item={speaker}
                        type="speaker"
                      />
                    ))}
                  </CardBody>
                </Card>
              )}

              {/* Specialists */}
              {webinar.specialists && webinar.specialists.length > 0 && (
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-3">Specialists</h5>
                    {webinar.specialists.map((specialist, index) => (
                      <SpeakerCard
                        key={specialist.specialist_id || index}
                        item={specialist}
                        type="specialist"
                      />
                    ))}
                  </CardBody>
                </Card>
              )}

              {/* Timestamps */}
              <Card className="mb-4">
                <CardBody>
                  <h5 className="card-title mb-3">Timestamps</h5>
                  <div className="mb-2">
                    <small className="text-muted">Created</small>
                    <p className="mb-0">{formatDate(webinar.created_at)}</p>
                  </div>
                  <div>
                    <small className="text-muted">Last Updated</small>
                    <p className="mb-0">{formatDate(webinar.updated_at)}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, webinar: null })}>
        <ModalHeader toggle={() => setDeleteModal({ show: false, webinar: null })}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the webinar &quot;{deleteModal.webinar?.title}&quot;?
          This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal({ show: false, webinar: null })}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email Options Modal */}
      <Modal isOpen={emailModal.show} toggle={() => setEmailModal({ show: false, webinar: null })} size="lg">
        <ModalHeader toggle={() => setEmailModal({ show: false, webinar: null })}>
          <i className="bx bx-envelope me-2"></i>
          Send Info to Participants
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">Choose the type of email to send to all approved participants for the webinar:</p>
          <div className="bg-light p-3 rounded mb-4">
            <strong>&quot;{emailModal.webinar?.title}&quot;</strong>
          </div>

          <Row>
            <Col md="4" className="mb-3">
              <Card
                className="h-100 cursor-pointer border-primary"
                style={{ cursor: 'pointer' }}
                onClick={() => showEmailConfirmation('WEBINAR_REMINDER', 'Email Reminder')}
              >
                <CardBody className="text-center">
                  <i className="bx bx-bell text-primary" style={{ fontSize: '2rem' }}></i>
                  <h6 className="mt-2 mb-1">Send Email Reminder</h6>
                  <p className="text-muted small mb-0">
                    Send reminder emails to participants about the upcoming webinar
                  </p>
                </CardBody>
              </Card>
            </Col>

            <Col md="4" className="mb-3">
              <Card
                className="h-100 cursor-pointer border-success"
                style={{ cursor: 'pointer' }}
                onClick={() => showEmailConfirmation('WEBINAR_STARTING', 'Webinar is On Going')}
              >
                <CardBody className="text-center">
                  <i className="bx bx-play-circle text-success" style={{ fontSize: '2rem' }}></i>
                  <h6 className="mt-2 mb-1">Webinar is On Going</h6>
                  <p className="text-muted small mb-0">
                    Notify participants that the webinar has started
                  </p>
                </CardBody>
              </Card>
            </Col>

            <Col md="4" className="mb-3">
              <Card
                className="h-100 cursor-pointer border-warning"
                style={{ cursor: 'pointer' }}
                onClick={() => showEmailConfirmation('POST_EVENT', 'Webinar is Done')}
              >
                <CardBody className="text-center">
                  <i className="bx bx-check-circle text-warning" style={{ fontSize: '2rem' }}></i>
                  <h6 className="mt-2 mb-1">Webinar is Done</h6>
                  <p className="text-muted small mb-0">
                    Send post-event emails to participants after webinar completion
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEmailModal({ show: false, webinar: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email Confirmation Modal */}
      <Modal isOpen={emailConfirmation.show} toggle={() => setEmailConfirmation({ show: false, webinar: null, triggerType: null, emailType: '' })}>
        <ModalHeader toggle={() => setEmailConfirmation({ show: false, webinar: null, triggerType: null, emailType: '' })}>
          <i className={emailConfirmation.triggerType === 'WEBINAR_STARTING' ? "bx bxl-whatsapp me-2" : "bx bx-envelope me-2"}></i>
          {emailConfirmation.triggerType === 'WEBINAR_STARTING' ? "Confirm Whatsapp" : "Confirm Email Sending"}
        </ModalHeader>
        <ModalBody>
          <p>
            {emailConfirmation.triggerType === 'WEBINAR_STARTING' ? (
              <>Are you sure you want to send <strong>{emailConfirmation.emailType}</strong> Whatsapp to all approved participants for the webinar:</>
            ) : (
              <>Are you sure you want to send <strong>{emailConfirmation.emailType}</strong> emails to all approved participants for the webinar:</>
            )}
          </p>
          <div className="bg-light p-3 rounded mb-3">
            <strong>&quot;{emailConfirmation.webinar?.title}&quot;</strong>
          </div>
          <p className="text-muted mb-0">
            {emailConfirmation.triggerType === 'WEBINAR_STARTING'
              ? "This will send Whatsapp to all users who have been approved for this webinar. This action cannot be undone."
              : "This will send emails to all users who have been approved for this webinar. This action cannot be undone."}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setEmailConfirmation({ show: false, webinar: null, triggerType: null, emailType: '' })}
          >
            Cancel
          </Button>
          <Button
            color="info"
            onClick={confirmSendBulkEmail}
            disabled={emailLoading}
          >
            {emailLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Sending...
              </>
            ) : (
              <>
                <i className={emailConfirmation.triggerType === 'WEBINAR_STARTING' ? "bx bxl-whatsapp me-1" : "bx bx-envelope me-1"}></i>
                {emailConfirmation.triggerType === 'WEBINAR_STARTING' ? "Send Whatsapp" : "Send Emails"}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(DetailWebinar);
